import { cn } from '@/client/utils';
import { keepsScroll } from '@/client/utils/keep-scroll';
import { ArrowUpToLineIcon } from 'lucide-react';
import { type RefObject, useEffect, useMemo, useState } from 'react';
import useLocation from 'react-use/lib/useLocation';
import useMedia from 'react-use/lib/useMedia';
import useScroll from 'react-use/lib/useScroll';
import useWindowScroll from 'react-use/lib/useWindowScroll';

type BackTopProps = {
  className?: string;
  showHeight?: number;
  minStep?: number;
  maxCount?: number;
  interval?: number;
  deviation?: number;
  parent?: RefObject<HTMLElement>;
};

export const ScrollTop = ({
  showHeight = 80,
  minStep = 80,
  maxCount = 5,
  interval = 60,
  deviation = 50,
  parent,
  ...attrs
}: BackTopProps) => {
  let history = 0;
  let top = 0;
  let start = Date.now();
  const isDesktop = useMedia('(min-width: 768px)');
  const scrollElement = parent ? parent.current : window.document.documentElement;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const scroll = parent ? useScroll(parent) : useWindowScroll();
  const location = useLocation();

  // The FAB sits over the card column's right edge (share/bookmark icons,
  // market counts), so it only stays up while the user is actually scrolling
  // and fades out (pointer-events off) after 2s idle — visible when useful,
  // never parked over a tap target.
  const [isScrolling, setIsScrolling] = useState(false);
  useEffect(() => {
    if (scroll.y <= showHeight) return;
    setIsScrolling(true);
    const timer = setTimeout(() => setIsScrolling(false), 2000);
    return () => clearTimeout(timer);
  }, [scroll.y, showHeight]);
  const isShow = useMemo(() => scroll.y > showHeight && isScrolling, [scroll.y, showHeight, isScrolling]);

  const scrollToTop = () => {
    if (Date.now() - start >= interval && scrollElement && scroll?.y) {
      if (Math.abs(scrollElement.scrollTop - history) <= deviation) {
        if (scrollElement.scrollTop > 0) {
          const y = scrollElement.scrollTop - Math.min(scrollElement.scrollTop, top);
          scrollElement.scroll(scrollElement.scrollLeft, y);
          history = scrollElement.scrollTop;
          start = Date.now();
          window.requestAnimationFrame(scrollToTop);
        }
      }
    } else window.requestAnimationFrame(scrollToTop);
  };

  const gotTop = () => {
    if (!scroll?.y) return;
    history = scrollElement?.scrollTop || 0;
    top = Math.max(Math.floor(history / maxCount), minStep);
    scrollToTop();
  };
  useEffect(() => {
    if (location && scrollElement) {
      // A location written by the 5-minute page with the keep-scroll marker keeps the position.
      // The marker stays on the history entry. A pop (Back, Forward) or a mount (reload, layout remount) into
      // that entry did not come from the page's own write, so it still scrolls to the top.
      if (location.trigger !== 'popstate' && location.trigger !== 'load' && keepsScroll(location.state)) return;
      scrollElement.scrollTop = 0;
    }
  }, [location, scrollElement]);

  if (isDesktop) return null;
  return (
    <aside
      className={cn(
        'pos-fixed bottom-0 right-4 z-50',
        'transition-all duration-200',
        'h-10 w-10 cursor-pointer select-none flex items-center justify-center bg-card rounded-full border  border-foreground/4 shadow-xl outline-none md:right-8',
        'hover:scale-105',
        attrs.className,
        isShow ? '-translate-y-32 opacity-90 md:-translate-y-8' : 'translate-y-32 opacity-0 md:translate-y-8'
      )}
      onClick={gotTop}
      {...attrs}
    >
      <ArrowUpToLineIcon className="size-4 fill-current" />
    </aside>
  );
};
